import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { CustomModalProps } from './types'
import CustomButton from 'components/customButton'
import Icon from '@zera-admin/icon'
import './CustomModal.scss'
import { zIndex } from 'html2canvas/dist/types/css/property-descriptors/z-index'

const modalRoot = document.getElementById('modal-root') || document.body

const CustomModal: React.FC<CustomModalProps> = ({
  isOpen = true,
  className,
  title,
  children,
  actions,
  onClose,
  onOpenComplete,
  onCloseComplete,
  width,
  height,
  appearance,
  scrollBehavior = 'inside',
  isBlanketHidden,
  center,
  centerBody = false,
  noPaddingBottom = false,
  backgroundColor,
  customOverflow = false,
  customButtonCenter = false,
  buttonStyle,
  style,
  isShowSaveButton = true,
  zIndex,
  ...rest
}) => {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      onOpenComplete?.()
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (!isOpen) onCloseComplete?.()
    }
  }, [isOpen])

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose?.()
    }
  }

  const [accoColor, setAccoColar] = useState('#449143')
  useEffect(() => {
    if (
      localStorage.getItem('facilityType') &&
      localStorage.getItem('facilityType') === '4'
    ) {
      setAccoColar('#D84027')
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      // ilk odaklanabilir scroll bölgesi
      requestAnimationFrame(() => {
        modalRef.current
          ?.querySelector<HTMLElement>('.focus-scroll-region')
          ?.focus({ preventScroll: true })
      })
    }
  }, [isOpen])

  return createPortal(
    <div
      className="custom-modal-overlay fade-in"
      onMouseDown={handleOverlayClick}
      role="presentation"
      tabIndex={-1}
      style={{ zIndex }}
    >
      <div
        ref={modalRef}
        className={classNames('custom-modal', className, {
          [`appearance-${appearance}`]: appearance,
          [`scroll-${scrollBehavior}`]: scrollBehavior,
        })}
        style={{
          width,
          height,
          overflow: customOverflow ? 'visible' : 'auto',
          ...style,
        }}
        {...rest}
      >
        <div
          className="custom-modal-header"
          style={{
            color: appearance === 'warning' ? '#f79e1b' : undefined,
            display: 'flex',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 2,
            background: '#fff',
            justifyContent: center ? 'center' : 'flex-start',
            borderRadius: customOverflow ? '8px 8px 0 0' : undefined,
          }}
        >
          {appearance === 'warning' && (
            <Icon name="warning" color="#f79e1b" style={{ marginRight: 8 }} />
          )}
          {/* {appearance === 'error' && (
            <Icon
              name="cross-circle"
              color="#ff5630"
              style={{ marginRight: 8 }}
            />
          )} */}
          {title && <h2 className="custom-modal-title">{title}</h2>}
          {onClose && (
            <CustomButton
              className="custom-modal-close-button"
              style={{
                border: `2px solid ${accoColor}`,
                borderRadius: '50%',
                color: accoColor,
              }}
              onClick={onClose}
            >
              <Icon size="small" name="cross" />
            </CustomButton>
          )}
        </div>
        <div
          className="custom-modal-body"
          style={{
            ...(centerBody ? { textAlign: 'center' } : {}),
            ...(noPaddingBottom ? { paddingBottom: 0 } : {}),
          }}
        >
          {children}
        </div>
        {isShowSaveButton && actions && actions.length > 0 && (
          <div
            className="custom-modal-footer"
            style={{
              display: customButtonCenter ? 'flex' : undefined,
              justifyContent: customButtonCenter ? 'center' : undefined,
              ...buttonStyle,
            }}
          >
            {actions.map((action: any, idx: any) =>
              action.render ? (
                action.render()
              ) : (
                <CustomButton
                  key={action.key || idx}
                  onClick={action.onClick}
                  color={action.appearance === 'danger' ? '#ff5630' : '#209947'}
                >
                  {action.text}
                </CustomButton>
              )
            )}
          </div>
        )}
      </div>
    </div>,
    modalRoot
  )
}

export default CustomModal
